// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import GameIcon from './GameIcon';
import * as ReputationManager from '../utils/game/ReputationManager';
import AlienManager from '../utils/game/AlienManager';
import { useCredits } from '../contexts/CreditsContext';
import { useShipHealth } from '../contexts/ShipHealthContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';
import OverlayGuard from '../utils/system/OverlayGuard';
import './AlienEncounterModal.css';

const AlienEncounterModal = ({ 
  encounter, 
  onClose, 
  onOutcome 
}) => {
  const [dialoguePhase, setDialoguePhase] = useState('initial'); // initial, response, outcome
  const [alienMessage, setAlienMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [outcomeResult, setOutcomeResult] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const { credits, addCredits, removeCredits } = useCredits();
  const { damageSystem } = useShipHealth();
  const { token, isAuthenticated } = useAuth();
  const [talkMode, setTalkMode] = useState(false);
  const [talkInput, setTalkInput] = useState('');
  const [talkMessages, setTalkMessages] = useState([]);
  const [talkLoading, setTalkLoading] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const talkEndRef = useRef(null);
  const audioRef = useRef(null);

  const { species, hostility, dialogue } = encounter;
  const reputation = ReputationManager.getReputation(species.id);
  const repLevel = ReputationManager.getReputationLevel(species.id);

  // Generate initial alien message based on reputation
  useEffect(() => {
    const initialMessage = getInitialMessage();
    setAlienMessage(initialMessage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register as an open overlay so global hotkey listeners (tab switching,
  // cheat codes) suppress themselves while this modal has focus — otherwise
  // typing a letter here (e.g. while the talk input briefly loses focus)
  // leaks through to those listeners. See OverlayGuard.js.
  useEffect(() => {
    const release = OverlayGuard.open();
    return release;
  }, []);

  useEffect(() => {
    talkEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [talkMessages]);

  const handleTalkQuery = async (text) => {
    if (!text.trim() || talkLoading) return;
    const question = text.trim();
    setTalkInput('');
    setTalkMessages(prev => [...prev, { role: 'user', text: question }]);
    setTalkLoading(true);

    const context = `The player is talking to ${species.name} (${species.origin}). ${species.description || ''}. Reputation: ${repLevel.label}. Respond in-character as this alien species in 2-3 sentences.`;
    const query = `${context}\n\nPlayer asks: ${question}`;

    let answer = getFallbackTalkResponse();
    try {
      if (!isAuthenticated) throw new Error('Not authenticated');
      const data = await api.kb.query(token, 'game', query, 3);
      let raw = data.answer || '';
      if (raw.includes('</think>')) raw = raw.split('</think>')[1].trim();
      if (raw) answer = raw;
    } catch {
      // fallback already set
    } finally {
      setTalkLoading(false);
    }

    setTalkMessages(prev => [...prev, { role: 'alien', text: answer }]);
    playTTS(answer);
  };

  const playTTS = async (text) => {
    if (!ttsEnabled || !isAuthenticated) return;
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      const url = await api.tts.speak(token, text);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      audio.play();
    } catch {
      // TTS is optional — fail silently
    }
  };

  const getFallbackTalkResponse = () => {
    const fallbacks = species.dialogue || [];
    if (fallbacks.length > 1) return fallbacks[Math.floor(Math.random() * fallbacks.length)];
    return `The ${species.name} regards you silently, unwilling to share more.`;
  };

  const getInitialMessage = () => {
    // Use reputation to determine tone
    if (reputation <= -50) {
      // ENEMY
      return species.dialogue[0] || "You dare approach us? Prepare for consequences!";
    } else if (reputation <= -10) {
      // UNFRIENDLY
      return "State your business quickly. We have little patience for your kind.";
    } else if (reputation >= 50) {
      // ALLIED
      return "Greetings, friend! It is good to see you again. How may we assist?";
    } else if (reputation >= 10) {
      // FRIENDLY
      return "Welcome, traveler. We are pleased to make contact with you.";
    } else {
      // NEUTRAL
      return dialogue || "Greetings, stranger. What brings you to our territory?";
    }
  };

  // Get available dialogue choices based on context
  const getDialogueChoices = () => {
    const choices = [];

    // FRIENDLY APPROACH - Always available unless ENEMY
    if (reputation > -50) {
      choices.push({
        id: 'friendly',
        icon: '🤝',
        label: 'Greet Peacefully',
        description: 'Attempt friendly contact',
        repChange: '+5 to +15',
        creditsChange: species.tradeable ? '+150 to +300' : '+100 to +200',
        color: '#00ff88',
        available: true
      });
    }

    // TRADE - Available if species is tradeable and not too hostile
    if (species.tradeable && hostility.level < 8) {
      const tradeBonus = reputation >= 50 ? '+30%' : reputation >= 10 ? '+15%' : '';
      choices.push({
        id: 'trade',
        icon: '💰',
        label: 'Offer Trade',
        description: 'Initiate trade negotiations',
        repChange: '+10',
        creditsChange: `+${species.tradeValue || 200}${tradeBonus}`,
        color: '#00ffff',
        available: true
      });
    }

    // AGGRESSIVE - Always available but consequences vary
    choices.push({
      id: 'aggressive',
      icon: '⚔️',
      label: hostility.level >= 8 ? 'Defend Yourself' : 'Show Force',
      description: hostility.level >= 8 ? 'Prepare for combat' : 'Intimidate them',
      repChange: species.hostile ? '+5' : '-10 to -15',
      creditsChange: '+100 (if victorious)',
      color: '#ff4444',
      available: true
    });

    // FLEE - Always available
    choices.push({
      id: 'flee',
      icon: '🏃',
      label: 'Leave Quickly',
      description: 'Retreat from encounter',
      repChange: '-5',
      creditsChange: '0',
      color: '#ffaa00',
      available: true
    });

    // SPECIAL: Request Aid - Only for ALLIED
    if (reputation >= 50) {
      choices.push({
        id: 'requestAid',
        icon: '🆘',
        label: 'Request Assistance',
        description: 'Ask for help from allies',
        repChange: '0',
        creditsChange: '+500 or repairs',
        color: '#00ff88',
        available: true,
        special: true
      });
    }

    // SPECIAL: Scan - If not hostile
    if (hostility.level < 7) {
      choices.push({
        id: 'scan',
        icon: '🔍',
        label: 'Scan Their Ship',
        description: 'Gather intelligence',
        repChange: '-5',
        creditsChange: '+50 (data)',
        color: '#00ffff',
        available: true,
        special: true
      });
    }

    // SPECIES-SPECIFIC CHOICES
    
    // VOGONS: Bureaucracy option
    if (species.id === 'vogons') {
      choices.push({
        id: 'fillPaperwork',
        icon: '📋',
        label: 'Fill Out Forms',
        description: 'Complete bureaucratic paperwork',
        repChange: '+15',
        creditsChange: '0',
        color: '#8b7355',
        available: true,
        special: true
      });
    }

    // MAGRATHEANS: Request planet design
    if (species.id === 'magratheans' && reputation >= 10) {
      choices.push({
        id: 'requestPlanet',
        icon: '🌍',
        label: 'Inquire About Planets',
        description: 'Ask about custom planet construction',
        repChange: '+10',
        creditsChange: '+200 (consultation fee)',
        color: '#c0c0c0',
        available: true,
        special: true
      });
    }

    // BABEL FISH: Request translation services
    if (species.id === 'babelfish') {
      choices.push({
        id: 'requestTranslation',
        icon: '🐟',
        label: 'Request Babel Fish',
        description: 'Acquire universal translator',
        repChange: '+5',
        creditsChange: '-500 (purchase)',
        color: '#ffff00',
        available: credits >= 500,
        special: true
      });
    }

    // CRYSTALLINE: Request shield technology
    if (species.id === 'crystalline' && reputation >= 30) {
      choices.push({
        id: 'requestShieldTech',
        icon: '🛡️',
        label: 'Request Shield Tech',
        description: 'Learn crystalline shield techniques',
        repChange: '+10',
        creditsChange: '-300',
        color: '#00ffff',
        available: credits >= 300,
        special: true
      });
    }

    // FERENGI: Haggle prices
    if (species.id === 'ferengi' && species.tradeable) {
      choices.push({
        id: 'haggle',
        icon: '💎',
        label: 'Haggle Prices',
        description: 'Negotiate for better trade terms',
        repChange: '+5',
        creditsChange: '+250 to +400',
        color: '#ffa500',
        available: true,
        special: true
      });
    }

    // KLINGONS: Challenge to honorable duel
    if (species.id === 'klingons') {
      choices.push({
        id: 'honorDuel',
        icon: '⚔️',
        label: 'Challenge to Duel',
        description: 'Honorable combat challenge',
        repChange: '+20 (if win)',
        creditsChange: '+300 (if win)',
        color: '#8b0000',
        available: true,
        special: true
      });
    }

    // VULCANS: Appeal to logic
    if (species.id === 'vulcans') {
      choices.push({
        id: 'appealLogic',
        icon: '🖖',
        label: 'Appeal to Logic',
        description: 'Use logical reasoning',
        repChange: '+15',
        creditsChange: '+100',
        color: '#4169e1',
        available: true,
        special: true
      });
    }

    return choices;
  };

  const handleChoiceSelect = (choice) => {
    setSelectedChoice(choice);
    setDialoguePhase('response');

    // Generate alien response
    const response = getAlienResponse(choice.id);
    setAlienMessage(response);

    // Process outcome after a delay
    setTimeout(() => {
      processOutcome(choice.id);
    }, 2000);
  };

  const getAlienResponse = (choiceId) => {
    switch (choiceId) {
      case 'friendly':
        if (reputation >= 50) {
          return "Your friendship is valued! Let us share knowledge and resources.";
        } else if (reputation >= 10) {
          return "We appreciate your peaceful approach. Perhaps we can help each other.";
        } else if (reputation <= -10) {
          return "Your words mean little. Prove your intentions with actions.";
        } else {
          return "Very well. We will observe your behavior carefully.";
        }

      case 'trade':
        if (reputation >= 50) {
          return "For our allies, we offer the best prices! Let us trade.";
        } else if (reputation >= 10) {
          return "We are willing to trade. You will find our prices fair.";
        } else {
          return "Trade is acceptable. But we will be watching you closely.";
        }

      case 'aggressive':
        if (species.hostile || hostility.level >= 8) {
          return "You dare threaten us? You will regret this!";
        } else if (hostility.level <= 3) {
          return "We mean no harm! Please, we will leave peacefully!";
        } else {
          return "So be it. If you want conflict, we will oblige!";
        }

      case 'flee':
        if (reputation >= 50) {
          return "Wait! There is no need to leave so hastily, friend!";
        } else if (hostility.level >= 7) {
          return "Run while you can, coward!";
        } else {
          return "Safe travels, stranger.";
        }

      case 'requestAid':
        return "Of course! Our alliance means we help each other. Here is what we can offer.";

      case 'scan':
        return "What are you doing? Stop scanning our vessel immediately!";

      // SPECIES-SPECIFIC RESPONSES
      case 'fillPaperwork':
        return "Ah, finally! Someone who understands proper procedure. Form 27B/6, section 3, paragraph 9...";

      case 'requestPlanet':
        return "Ah, a customer! We haven't had one in five million years. Let me show you our catalog...";

      case 'requestTranslation':
        return "*The Babel Fish wriggles happily* Universal translation is now yours, traveler.";

      case 'requestShieldTech':
        return "Our crystalline matrix technology is legendary. We will share our knowledge with you.";

      case 'haggle':
        return "Ah, a fellow merchant! Very well, let us negotiate. Rule of Acquisition #3: Never pay more than you have to!";

      case 'honorDuel':
        return "Qapla'! You have honor! Let us test our skills in glorious combat!";

      case 'appealLogic':
        return "Your logic is sound. It would be illogical to refuse cooperation. Live long and prosper.";

      default:
        return "...";
    }
  };

  const processOutcome = (choiceId) => {
    let result;

    switch (choiceId) {
      case 'friendly':
        result = AlienManager.processFriendlyInteraction(encounter, { credits });
        break;
      case 'trade':
        result = AlienManager.processTrade(encounter, { credits });
        break;
      case 'aggressive':
        result = AlienManager.processAggressiveAction(encounter, { weaponPower: 50 });
        break;
      case 'flee':
        result = AlienManager.processEvasion(encounter, {});
        break;
      case 'requestAid':
        result = {
          success: true,
          outcome: 'AID_RECEIVED',
          message: `The ${species.name} provide assistance! They offer supplies and repairs.`,
          credits: 500,
          damage: -20, // Negative damage = healing
          color: '#00ff88',
          reputationChange: 0
        };
        break;
      case 'scan':
        result = {
          success: true,
          outcome: 'SCAN_COMPLETE',
          message: `You gather valuable intelligence about the ${species.name}.`,
          credits: 50,
          damage: 0,
          color: '#00ffff',
          reputationChange: -5
        };
        break;

      // SPECIES-SPECIFIC OUTCOMES
      case 'fillPaperwork':
        result = {
          success: true,
          outcome: 'PAPERWORK_COMPLETE',
          message: 'The Vogons are impressed by your bureaucratic compliance! They let you pass without incident.',
          credits: 0,
          damage: 0,
          color: '#8b7355',
          reputationChange: 15
        };
        break;

      case 'requestPlanet':
        result = {
          success: true,
          outcome: 'PLANET_CONSULTATION',
          message: 'The Magratheans share their legendary planet-building knowledge. You receive a consultation fee!',
          credits: 200,
          damage: 0,
          color: '#c0c0c0',
          reputationChange: 10
        };
        break;

      case 'requestTranslation':
        result = {
          success: true,
          outcome: 'BABEL_FISH_ACQUIRED',
          message: 'You acquire a Babel Fish! Universal translation is now available. (Future feature)',
          credits: -500,
          damage: 0,
          color: '#ffff00',
          reputationChange: 5
        };
        break;

      case 'requestShieldTech':
        result = {
          success: true,
          outcome: 'SHIELD_TECH_LEARNED',
          message: 'The Crystalline beings share their shield matrix technology! Your shields are enhanced. (Future feature)',
          credits: -300,
          damage: 0,
          color: '#00ffff',
          reputationChange: 10
        };
        break;

      case 'haggle':
        const haggleSuccess = Math.random() < 0.7; // 70% success rate
        if (haggleSuccess) {
          const haggleProfit = Math.floor(250 + Math.random() * 150); // 250-400 credits
          result = {
            success: true,
            outcome: 'HAGGLE_SUCCESS',
            message: `The Ferengi respect your negotiation skills! You strike a profitable deal.`,
            credits: haggleProfit,
            damage: 0,
            color: '#ffa500',
            reputationChange: 5
          };
        } else {
          result = {
            success: false,
            outcome: 'HAGGLE_FAILED',
            message: 'The Ferengi out-negotiate you. No deal today.',
            credits: 0,
            damage: 0,
            color: '#ff8800',
            reputationChange: -5
          };
        }
        break;

      case 'honorDuel':
        const duelSuccess = Math.random() < 0.5; // 50% win rate
        if (duelSuccess) {
          result = {
            success: true,
            outcome: 'DUEL_VICTORY',
            message: 'Qapla\'! You win the honorable duel! The Klingons respect your warrior spirit!',
            credits: 300,
            damage: 15, // Take some damage even when winning
            color: '#8b0000',
            reputationChange: 20
          };
        } else {
          result = {
            success: false,
            outcome: 'DUEL_DEFEAT',
            message: 'You fought with honor but were defeated. The Klingons still respect your courage.',
            credits: 0,
            damage: 30,
            color: '#ff4444',
            reputationChange: 5
          };
        }
        break;

      case 'appealLogic':
        result = {
          success: true,
          outcome: 'LOGIC_ACCEPTED',
          message: 'The Vulcans find your reasoning logical and sound. They share knowledge and resources.',
          credits: 100,
          damage: 0,
          color: '#4169e1',
          reputationChange: 15
        };
        break;

      default:
        result = { success: false, outcome: 'UNKNOWN', message: 'Unknown action', credits: 0, damage: 0, color: '#888', reputationChange: 0 };
    }

    // Apply credits
    if (result.credits > 0) {
      addCredits(result.credits);
    } else if (result.credits < 0) {
      removeCredits(Math.abs(result.credits));
    }

    // Apply damage (or healing)
    if (result.damage > 0) {
      damageSystem('HULL', result.damage);
    } else if (result.damage < 0) {
      // Healing would go here if we had a heal function
    }

    setOutcomeResult(result);
    setDialoguePhase('outcome');
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      if (onOutcome && outcomeResult) {
        onOutcome(outcomeResult);
      }
      onClose();
    }, 300);
  };

  const dialogueChoices = getDialogueChoices();

  return createPortal(
    <div className={`alien-encounter-modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="alien-encounter-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="alien-modal-header">
          <div className="alien-modal-title">◊ ALIEN COMMUNICATION CHANNEL ◊</div>
          <button className="alien-modal-close" onClick={handleClose}>✕</button>
        </div>

        {/* Main Content */}
        <div className="alien-modal-content">
          {/* Left Side - Alien Portrait & Info */}
          <div className="alien-portrait-section">
            <div className="alien-portrait-container">
              <div className="alien-portrait-icon">
                <GameIcon 
                  type="alien" 
                  name={species.id} 
                  size={120} 
                  color={species.appearance.color}
                  glow={true}
                />
              </div>
              <div className="alien-portrait-scanlines"></div>
            </div>

            <div className="alien-species-info">
              <div className="alien-species-name" style={{ color: species.appearance.color }}>
                {species.name.toUpperCase()}
              </div>
              <div className="alien-origin">{species.origin}</div>
            </div>

            {/* Reputation Display */}
            <div className="alien-reputation-display">
              <div className="reputation-label">REPUTATION</div>
              <div className="reputation-level-badge" style={{ 
                borderColor: repLevel.color,
                color: repLevel.color
              }}>
                {repLevel.label}
              </div>
              <div className="reputation-score" style={{ color: repLevel.color }}>
                {reputation > 0 ? '+' : ''}{reputation}
              </div>
              <div className="reputation-bar-mini">
                <div 
                  className="reputation-bar-fill-mini" 
                  style={{ 
                    width: `${ReputationManager.getReputationPercentage(species.id)}%`,
                    background: repLevel.color
                  }}
                />
              </div>
            </div>

            {/* Hostility Indicator */}
            <div className="alien-hostility-display">
              <div className="hostility-label">THREAT LEVEL</div>
              <div className="hostility-badge" style={{ 
                borderColor: hostility.color,
                color: hostility.color
              }}>
                {hostility.category} ({hostility.level}/10)
              </div>
            </div>
          </div>

          {/* Right Side - Dialogue & Choices */}
          <div className="alien-dialogue-section">
            {/* Alien Message */}
            <div className="alien-message-container">
              <div className="alien-message-header">
                <span className="alien-message-icon">
                  <GameIcon 
                    type="alien" 
                    name={species.id} 
                    size={32} 
                    color={species.appearance.color}
                    glow={true}
                  />
                </span>
                <span className="alien-message-from">{species.name}</span>
              </div>
              <div className="alien-message-text">
                "{alienMessage}"
              </div>
            </div>

            {/* Dialogue Choices */}
            {dialoguePhase === 'initial' && (
              <div className="dialogue-choices">
                <div className="choices-header">YOUR RESPONSE:</div>
                <div className="choices-grid">
                  {dialogueChoices.map(choice => (
                    <button
                      key={choice.id}
                      className={`dialogue-choice ${choice.special ? 'special' : ''}`}
                      onClick={() => handleChoiceSelect(choice)}
                      disabled={!choice.available}
                      style={{
                        borderColor: choice.color,
                        opacity: choice.available ? 1 : 0.5
                      }}
                    >
                      <div className="choice-header">
                        <span className="choice-icon">{choice.icon}</span>
                        <span className="choice-label">{choice.label}</span>
                      </div>
                      <div className="choice-description">{choice.description}</div>
                      <div className="choice-consequences">
                        <span className="choice-rep">Rep: {choice.repChange}</span>
                        <span className="choice-credits">{choice.creditsChange}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Talk to Alien */}
                {!talkMode ? (
                  <button
                    className="dialogue-choice special talk-toggle-btn"
                    onClick={() => setTalkMode(true)}
                    style={{ borderColor: species.appearance.color, marginTop: '8px', width: '100%' }}
                  >
                    <div className="choice-header">
                      <span className="choice-icon">💬</span>
                      <span className="choice-label">Talk to {species.name}</span>
                    </div>
                    <div className="choice-description">Ask questions using the ship computer</div>
                  </button>
                ) : (
                  <div className="alien-talk-section" style={{ borderColor: species.appearance.color }}>
                    <div className="alien-talk-header">
                      <span className="alien-talk-title">◈ {species.name} COMMS</span>
                      <button
                        className="alien-talk-tts-toggle"
                        onClick={() => setTtsEnabled(prev => !prev)}
                        title={ttsEnabled ? 'Mute voice' : 'Enable voice'}
                        style={{ color: ttsEnabled ? species.appearance.color : '#555' }}
                      >
                        {ttsEnabled ? '🔊' : '🔇'}
                      </button>
                    </div>
                    <div className="alien-talk-messages">
                      {talkMessages.map((m, i) => (
                        <div key={i} className={`alien-talk-msg alien-talk-${m.role}`}>
                          <span className="alien-talk-prefix">{m.role === 'user' ? '▶' : '◈'}</span>
                          <span className="alien-talk-text">{m.text}</span>
                        </div>
                      ))}
                      {talkLoading && (
                        <div className="alien-talk-msg alien-talk-alien">
                          <span className="alien-talk-prefix">◈</span>
                          <span className="alien-talk-text alien-talk-loading">Translating<span className="sc-dots">...</span></span>
                        </div>
                      )}
                      <div ref={talkEndRef} />
                    </div>
                    <div className="alien-talk-input-row">
                      <input
                        className="alien-talk-input"
                        value={talkInput}
                        onChange={e => setTalkInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleTalkQuery(talkInput); }}
                        placeholder={`Ask ${species.name} something...`}
                        disabled={talkLoading}
                        autoFocus
                      />
                      <button
                        className="alien-talk-send"
                        onClick={() => handleTalkQuery(talkInput)}
                        disabled={talkLoading || !talkInput.trim()}
                        style={{ borderColor: species.appearance.color, color: species.appearance.color }}
                      >ASK</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Response Phase */}
            {dialoguePhase === 'response' && (
              <div className="dialogue-response">
                <div className="response-processing">
                  <div className="processing-spinner"></div>
                  <div className="processing-text">Processing response...</div>
                </div>
              </div>
            )}

            {/* Outcome Phase */}
            {dialoguePhase === 'outcome' && outcomeResult && (
              <div className="dialogue-outcome">
                <div className="outcome-header" style={{ color: outcomeResult.color }}>
                  {outcomeResult.success ? '✓ SUCCESS' : '✗ FAILURE'}
                </div>
                <div className="outcome-message">{outcomeResult.message}</div>
                <div className="outcome-details">
                  {outcomeResult.credits !== 0 && (
                    <div className="outcome-detail">
                      <span className="outcome-icon">💰</span>
                      <span className="outcome-value" style={{ color: outcomeResult.credits > 0 ? '#00ff88' : '#ff4444' }}>
                        {outcomeResult.credits > 0 ? '+' : ''}{outcomeResult.credits} Credits
                      </span>
                    </div>
                  )}
                  {outcomeResult.damage !== 0 && (
                    <div className="outcome-detail">
                      <span className="outcome-icon">💥</span>
                      <span className="outcome-value" style={{ color: '#ff4444' }}>
                        {outcomeResult.damage} Damage
                      </span>
                    </div>
                  )}
                  {outcomeResult.reputationChange !== 0 && (
                    <div className="outcome-detail">
                      <span className="outcome-icon">🤝</span>
                      <span className="outcome-value" style={{ color: outcomeResult.reputationChange > 0 ? '#00ff88' : '#ff4444' }}>
                        {outcomeResult.reputationChange > 0 ? '+' : ''}{outcomeResult.reputationChange} Reputation
                      </span>
                    </div>
                  )}
                </div>
                <Button 
                  variant="primary" 
                  onClick={handleClose}
                  style={{ marginTop: '20px' }}
                >
                  CONTINUE
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AlienEncounterModal;
